import { useEffect, useState } from 'react';
import { useStore } from 'zustand';

import { type Entity, type EntityId, type SceneDocument } from '@/domain/model';
import { type EditorStore } from '@/store';

import { PanelSection } from './Inspector';
import { colour } from './theme';

/**
 * Options for the selected angle marks.
 *
 * Appears only when one is selected. There are exactly two things to change, and both are
 * shown at once rather than behind a menu — with two options, a menu is a click that buys
 * nothing.
 *
 * ## What is not here
 *
 * There is no "make this a right angle" switch. Whether the square is drawn is decided by the
 * angle itself, every time it is evaluated: mark an angle, drag it to 90°, and the square
 * appears. A control that forced the square would let a user put one on an 87° angle, and a
 * diagram that says a thing is right when it is not is worse than one with no mark at all.
 *
 * The panel says so, because a missing control is a question a user will otherwise ask.
 */

type AngleEntity = Entity & { readonly source: Extract<Entity['source'], { op: 'angle-mark' }> };

export interface AnglePanelProps {
  readonly store: EditorStore;
  /**
   * Lights a point up on the canvas without selecting it.
   *
   * The panel names three points, and a name is no use if the reader cannot tell which mark on
   * the drawing it belongs to — most of them have no label at all. Pointing at a name lights the
   * point; selecting it instead would close this panel and take the question away.
   */
  readonly onHighlight?: (ids: readonly EntityId[]) => void;
}

const TICKS = [1, 2, 3] as const;

/**
 * Arc sizes, as multiples of the ordinary one.
 *
 * Three steps rather than a slider: the only reason to change this is that a *projected*
 * figure has closed the angle up until the number naming it no longer fits between the arms,
 * and for that a few distinct sizes are enough. A slider would invite fiddling with something
 * that should be uniform across a page.
 */
const SIZES = [
  { label: 'vừa', value: 1 },
  { label: 'to', value: 1.7 },
  { label: 'rất to', value: 2.6 },
] as const;

/**
 * The one point a mark's angle is free to move, when exactly one is.
 *
 * Its own function because the panel needs it *before* it may return early, and a hook cannot be
 * called conditionally. `undefined` when nothing is selected, when several points are free, or
 * when none is — all of which mean "no single point to light up".
 */
function chosenArm(
  document: SceneDocument,
  points: { a: EntityId; vertex: EntityId; c: EntityId } | null
): EntityId | undefined {
  if (points === null) return undefined;

  const free = [points.c, points.a, points.vertex].filter((armId) => {
    const entity = document.entities.get(armId);
    if (entity === undefined || entity.locked) return false;
    return entity.source.op === 'free-point' || entity.source.op === 'ratio-point';
  });

  /*
   * The *first* of them, in the order the motion prefers, not "the one when there is only one".
   *
   * Exactly one point ever moves — a figure is dragged by hand one point at a time — so there is
   * always an answer to "which one", and the panel must be able to give it before anything is
   * locked. Showing nothing until two of the three were held was the version that left a user
   * unable to tell what a number would do.
   */
  return free[0];
}

export function AnglePanel({ store, onHighlight }: AnglePanelProps) {
  const document = useStore(store, (state) => state.document);
  const scene = useStore(store, (state) => state.scene);
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  const marks = [...selection]
    .map((id) => document.entities.get(id))
    .filter((entity): entity is AngleEntity => entity?.source.op === 'angle-mark');

  const first = marks[0];
  const [draft, setDraft] = useState<string | null>(null);

  /*
   * The chosen point stays lit for as long as this panel is open.
   *
   * "tôi đang không biết AOC là điểm nào" — a name is no use if the reader cannot tell which
   * mark it belongs to, and most points carry no label. Hovering a name previews it; the point
   * actually chosen stays lit, so the answer is on the drawing rather than in a tooltip.
   *
   * Worked out here, above the early return, because a hook may not be called conditionally —
   * and `undefined` when there is no mark is the right answer anyway.
   */
  /*
   * The mark's three **points**, whichever way its arms were given.
   *
   * An arm may be a point or the side itself — the angle tool accepts both, because the thing a
   * person points at when they say "this angle" is the two sides. Naming a segment in this panel
   * would light up a whole line where the reader expects a corner, and would offer to move
   * something that cannot be moved.
   */
  const points = first === undefined ? null : commands.pointsOfAngle(first.id);
  const lit = chosenArm(document, points);
  useEffect(() => {
    onHighlight?.(lit === undefined ? [] : [lit]);
    return () => onHighlight?.([]);
  }, [lit, onHighlight]);

  if (first === undefined) return null;

  const ids = marks.map((entity) => entity.id);

  /** A value the whole selection agrees on, or undefined when it does not. */
  const shared = <T,>(read: (entity: AngleEntity) => T): T | undefined => {
    const value = read(first);
    return marks.every((entity) => read(entity) === value) ? value : undefined;
  };

  /*
   * The number the angle is *held* at, when it is held at all, and what it currently measures.
   *
   * Two different things, and the field shows the first while the second is its placeholder —
   * so an unheld angle offers its own measurement as the obvious thing to type.
   */
  const fixed = shared((entity) => commands.fixedAngleOf(entity.id));
  /*
   * Whether a number would do anything at all.
   *
   * An angle whose three points are all *derived* is settled by whatever built them — the solver
   * has no variable to turn. Taking the number anyway and quietly doing nothing is exactly how
   * this field came to look broken, so it is greyed out instead, with the reason on it.
   */
  const settable = marks.every((entity) => commands.canSetAngle(entity.id));
  /*
   * The angles a sliding point can actually make.
   *
   * A point on an edge runs from one end of it to the other and the angle runs with it, between
   * two bounds. Without the range, a number outside it is simply refused and the user has no way
   * to know which numbers would not be — the refusal has to carry the answer with it.
   */
  const reach = marks.length === 1 ? commands.angleReachOf(first.id) : null;
  const measured = (() => {
    const value = scene.values.get(first.id);
    return value?.kind === 'angle-mark' ? value.degrees : null;
  })();

  /*
   * The three points of the mark, with whether the solver may move each.
   *
   * A *derived* point is not a variable at all — Tier 2 may never write to what Tier 1 built —
   * so it is shown as immovable rather than as an unlocked one that mysteriously never moves.
   */
  const arms = [points?.a, points?.vertex, points?.c].flatMap((armId, index) => {
    if (armId === undefined) return [];
    const entity = document.entities.get(armId);
    return [
      {
        id: armId,
        /*
         * The point's own name when it has one, and its **role** when it does not.
         *
         * Never an invented letter. The first version showed `A`, `O`, `C` for unlabelled points,
         * which named three things that appear nowhere on the drawing — the reader is then worse
         * off than with no names, because they have been given something to look for that is not
         * there. "cạnh 1", "đỉnh", "cạnh 2" are true of every angle whether or not it is lettered.
         */
        name: entity?.label ?? ['cạnh 1', 'đỉnh', 'cạnh 2'][index] ?? '?',
        locked: entity?.locked === true,
        /*
         * A point that may be put somewhere else — anywhere for a free one, anywhere *on its own
         * edge* for one built as a division of two others. A solid figure only has the second, and
         * it is the better kind: however far it slides it cannot leave the figure.
         */
        movable: entity?.source.op === 'free-point' || entity?.source.op === 'ratio-point',
      },
    ];
  });
  const movable = arms.filter((arm) => arm.movable && !arm.locked);

  /**
   * What is being typed, while it is being typed.
   *
   * `null` means the field simply shows the angle. See the note on the input for why a keystroke
   * must not reach the figure.
   */
  const commit = (text: string | null): void => {
    setDraft(null);
    if (text === null) return;

    // An empty box is not a request for a zero-degree angle; it is no request at all.
    if (text.trim() === '') {
      commands.setAngleValue(ids, null);
      return;
    }

    const typed = Number(text);
    if (Number.isFinite(typed) && typed > 0 && typed < 180) commands.setAngleValue(ids, typed);
  };

  /** The point the next number will move, which the controller decides and this only reports. */
  const moverId = marks.length === 1 ? commands.angleMoverOf(first.id) : null;
  const chosen = arms.find((arm) => arm.id === moverId);

  /*
   * Choosing a point says *that one moves*, and nothing else.
   *
   * Two earlier versions of this control were wrong in opposite directions. The first made each
   * name a lock **toggle**, so pressing the point you wanted to move pinned it — backwards from
   * the heading above it. The second locked the other two, which said the right thing and did
   * far too much: `hitTestAll` skips a locked entity, so choosing which arm an angle moved left
   * the other two unclickable everywhere in the application.
   *
   * It is a preference now. Nothing is written to the document, nothing is locked, and nothing
   * outside this one angle notices.
   */
  const choose = (armId: EntityId): void => {
    if (points === null) return;
    const which = armId === points.a ? 'a' : armId === points.c ? 'c' : 'vertex';
    commands.setAngleMover(first.id, which);
  };

  const ticks = shared((entity) => entity.source.ticks);
  const size = shared((entity) => entity.source.size ?? 1);
  const showValue = shared((entity) => entity.source.showValue);

  // How many of the selected marks have had their number carried somewhere by hand.
  const moved = marks.filter((entity) => {
    const offset = entity.source.valueOffset;
    return offset !== undefined && (offset.x !== 0 || offset.y !== 0);
  }).length;

  // Whether the square is currently drawn — read from the evaluated mark, which is the only
  // thing that knows, and shown so the user can see the rule rather than be told it.
  const rightAngles = marks.filter((entity) => {
    const value = scene.values.get(entity.id);
    return value?.kind === 'angle-mark' && value.isRight;
  }).length;

  return (
    <PanelSection
      title="Ký hiệu góc"
      {...(marks.length === 1 ? {} : { badge: String(marks.length) })}
    >
      {/*
       * Typing here does not edit the caption. The number beside a mark has never been data — it
       * is the angle, measured — so a field that overwrote it would let a figure say 60° while
       * being 47°. It sets a **constraint** instead, and the solver moves the drawing until the
       * angle really is that; the caption then reads 60° because the angle is 60°.
       */}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: colour.muted, fontSize: 11 }}>Số đo (độ)</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            type="number"
            min={0}
            max={180}
            step={1}
            disabled={!settable}
            title={
              settable
                ? 'Hình sẽ tự chỉnh về đúng góc này'
                : 'Ba điểm của góc này đều là điểm dựng, nên góc do hình khác quyết định'
            }
            value={
              draft ??
              (fixed === undefined || fixed === null ? '' : String(Math.round(fixed * 100) / 100))
            }
            placeholder={measured === null ? '' : String(Math.round(measured * 10) / 10)}
            /*
             * Typed into a draft, applied when the typing stops.
             *
             * Applying on every keystroke reshapes the drawing for each digit: typing `90` sets
             * the angle to 9° and snaps the figure there before the second key arrives. Worse,
             * the field is controlled, so what it shows after that first keystroke is whatever
             * the document now says — and the number under the cursor changes while the user is
             * still typing it.
             *
             * Enter and leaving the field both commit; Escape gives up and shows the angle as it
             * stands.
             */
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                commit(draft);
                event.currentTarget.blur();
              }
              if (event.key === 'Escape') {
                setDraft(null);
                event.currentTarget.blur();
              }
            }}
            onBlur={() => {
              commit(draft);
            }}
            className="es-field"
            style={{ flex: 1, minWidth: 0 }}
          />
          {fixed !== undefined && fixed !== null && (
            <button
              type="button"
              title="Thả góc ra, hình giữ nguyên hình dạng hiện tại"
              onClick={() => {
                commands.setAngleValue(ids, null);
              }}
              className="es-btn"
              style={{ padding: '4px 8px', fontSize: 11 }}
            >
              Thả
            </button>
          )}
        </div>
        <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
          {reach !== null
            ? `Đặt được từ ${String(Math.ceil(reach.low))}° đến ${String(Math.floor(reach.high))}° — điểm sẽ chạy trên cạnh của nó.`
            : fixed === undefined || fixed === null
              ? 'Nhập một số để hình tự chỉnh về đúng góc đó.'
              : 'Góc đang được giữ ở số này.'}
        </p>
      </label>

      {/*
       * Which of the three points the solver may move.
       *
       * "nhưng tôi không biết điểm nào được chọn để di chuyển" — and there was no way to know:
       * least squares spreads the correction, so typing an angle nudged all three by a little,
       * invisibly. The answer is not a cleverer default but *showing* it, and letting the user
       * say otherwise in the same place they typed the number.
       *
       * It is the ordinary lock, not a private setting: a locked point is a constant to the
       * solver, it reads the same in the object list, and unlocking it here unlocks it
       * everywhere. One idea, visible in every place it applies.
       */}
      {marks.length === 1 && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ color: colour.muted, fontSize: 11 }}>Điểm được di chuyển</span>
          <div style={{ display: 'flex', gap: 2 }}>
            {arms.map((arm) => (
              <button
                key={arm.id}
                type="button"
                disabled={!arm.movable || arm.locked}
                onMouseEnter={() => onHighlight?.([arm.id])}
                onMouseLeave={() => onHighlight?.([chosen?.id].filter((one) => one !== undefined))}
                onFocus={() => onHighlight?.([arm.id])}
                onBlur={() => onHighlight?.([chosen?.id].filter((one) => one !== undefined))}
                title={
                  arm.locked
                    ? `${arm.name} đang bị khoá — mở khoá bằng chuột phải trên hình`
                    : arm.movable
                      ? `Chỉ ${arm.name} dịch chuyển; hai điểm kia giữ nguyên`
                      : `${arm.name} là điểm dựng, luôn đứng yên theo hình của nó`
                }
                onClick={() => {
                  choose(arm.id);
                  onHighlight?.([arm.id]);
                }}
                className={chosen?.id === arm.id ? 'es-btn es-btn--on' : 'es-btn'}
                aria-pressed={chosen?.id === arm.id}
                style={{ flex: 1, padding: '4px 2px', fontSize: 11 }}
              >
                {arm.name}
              </button>
            ))}
          </div>
          <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
            {movable.length === 0
              ? 'Ba điểm này đều cố định theo hình khác, nên góc không đặt số được. Hãy lấy một điểm chạy trên cạnh (ví dụ trung điểm hoặc điểm chia) rồi đánh dấu góc qua nó.'
              : chosen === undefined
                ? 'Không điểm nào được dịch — bấm một tên ở trên để thả nó ra.'
                : `Chỉ ${chosen.name} dịch chuyển, đúng như kéo tay một điểm. Nó đang sáng trên hình.`}
          </p>
        </label>
      )}

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: colour.muted, fontSize: 11 }}>Cỡ cung</span>
        <div style={{ display: 'flex', gap: 2 }}>
          {SIZES.map((option) => (
            <button
              key={option.label}
              type="button"
              title={`Cung ${option.label} — dùng khi hình chiếu làm góc hẹp lại`}
              onClick={() => {
                commands.setAngleMark(ids, { size: option.value });
              }}
              className={size === option.value ? 'es-btn es-btn--on' : 'es-btn'}
              aria-pressed={size === option.value}
              style={{ flex: 1, padding: '4px 2px', fontSize: 11 }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ color: colour.muted, fontSize: 11 }}>Số cung (góc bằng nhau)</span>
        <div style={{ display: 'flex', gap: 2 }}>
          {TICKS.map((count) => (
            <button
              key={count}
              type="button"
              title={`${String(count)} cung`}
              onClick={() => {
                commands.setAngleMark(ids, { ticks: count });
              }}
              className={ticks === count ? 'es-btn es-btn--on' : 'es-btn'}
              aria-pressed={ticks === count}
              style={{ flex: 1, padding: '4px 2px', fontSize: 11 }}
            >
              {'⌒'.repeat(count)}
            </button>
          ))}
        </div>
      </label>

      <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={showValue ?? false}
          onChange={(event) => {
            commands.setAngleMark(ids, { showValue: event.target.checked });
          }}
        />
        <span style={{ color: colour.body }}>Hiện số đo</span>
      </label>

      {/*
       * Only once there is a number to move, and only once it has been moved.
       *
       * A control that undoes something the user has not done is a control they have to read
       * and dismiss. The hint below is the discoverable half — dragging a number is not a
       * gesture anyone tries unprompted — and this is the way back, which matters more than
       * usual here: a number dragged off the page is otherwise found only by undoing.
       */}
      {showValue === true && (
        <>
          <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
            Kéo số đo hoặc nhãn góc để đặt vào chỗ vừa ý.
          </p>
          {moved > 0 && (
            <button
              type="button"
              onClick={() => {
                commands.setAngleMark(ids, { valueOffset: { x: 0, y: 0 } });
              }}
              className="es-btn"
              style={{ padding: '4px 8px', fontSize: 11 }}
            >
              Đưa số đo về chỗ cũ
            </button>
          )}
        </>
      )}

      <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
        {rightAngles === marks.length
          ? 'Đang là góc vuông — ký hiệu là ô vuông. Kéo cho khác 90° thì tự thành cung.'
          : rightAngles > 0
            ? `${String(rightAngles)}/${String(marks.length)} đang vuông. Ô vuông chỉ hiện khi góc đúng 90°.`
            : 'Ô vuông tự hiện khi góc đạt đúng 90°. Số cung không ảnh hưởng tới góc vuông.'}
      </p>
    </PanelSection>
  );
}
